import type { PartialMessage } from '@bufbuild/protobuf'
import { useMutation } from '@connectrpc/connect-query'
import { FormFieldType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/form_field_type_pb'
import { createFormResponsePublic } from '@fingertip/creator-proto/gen/fingertip/creator/form_response/v1/form_response-FormResponseService_connectquery'
import type { GetFormTemplatePublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/form_template/v1/form_template_pb'
import { ArrowRightIcon, CheckIcon } from '@fingertip/icons'
import { ReactNode, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { BlockHeading } from '@/components/form-template/inputs/block-heading'
import { DynamicInput } from '@/components/form-template/inputs/dynamic-input'
import { PageHeader } from '@/components/page-header/page-header'
import { Button } from '@/components/ui/button'
import { useToken } from '@/lib/hooks/use-token'
import { flattenObject } from '@/lib/utils/utils'

import { AlertBanner } from '../shared/alert-banner'

type Props = {
  siteSlug: string
  formTemplateSlug: string
  source: string
  formTemplateData: PartialMessage<GetFormTemplatePublicResponse>
  breadCrumbs?: ReactNode
}

export const PublicFormForm = ({
  siteSlug,
  formTemplateSlug,
  source,
  formTemplateData,
  breadCrumbs,
}: Props) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState<'STARTED' | 'SUBMITTED'>('STARTED')
  const { callOptions } = useToken()
  const themeContent =
    formTemplateData?.formTheme?.content || formTemplateData?.pageTheme?.content

  const fields = formTemplateData?.formFields || []

  const form = useForm({
    defaultValues: {},
  })

  const mutationCreate = useMutation(createFormResponsePublic, {
    callOptions,
    onSuccess: () => {
      toast.success(t('form_submitted_successfully'))
      setStatus('SUBMITTED')
      form.reset()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = async (data: Record<string, string>) => {
    const flattenedObject = flattenObject(data)

    mutationCreate.mutate({
      input: flattenedObject,
      formTemplateSlug,
      source,
      siteSlug,
    })
  }

  if (!formTemplateData?.formTemplate) {
    return (
      <div className="py-fluid-4">
        <AlertBanner>{t('no_form_template_found')}</AlertBanner>
      </div>
    )
  }

  const page = {
    name: formTemplateData?.formTemplate?.title,
    description: formTemplateData?.formTemplate?.description,
    bannerMedia: formTemplateData?.formTemplate?.bannerMedia,
  }

  let header = (
    <>
      <PageHeader
        page={page}
        headerSlug={themeContent?.headerSlug}
        headerImageStyle={themeContent?.headerImageStyle}
      />

      {breadCrumbs}
    </>
  )

  let content = null

  if (status === 'SUBMITTED') {
    content = (
      <div className="px-fluid-4">
        <div className="ft-widget-wrapper size-full rounded-page-widget text-neutral-900">
          <div className="bg-page-widget-background shadow-page-widget border-page-widget size-full rounded-page-widget text-page-body-text backdrop-blur-page-widget">
            <div className="p-block flex size-full flex-col items-center justify-center">
              <h3 className="mb-fluid-4 text-center text-lg">
                {formTemplateData?.formTemplate?.thankYouMessage ||
                  t('thank_you_for_your_submission')}
              </h3>

              <div className="flex items-center">
                <Button
                  type="button"
                  variant="blockPrimary"
                  size="block"
                  onClick={() => setStatus('STARTED')}
                >
                  <span className="mr-fluid-2">{t('reload')}</span>
                  <CheckIcon />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  } else {
    content = (
      <div className="px-fluid-4">
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-fluid-3"
          id={`form-${formTemplateData?.formTemplate?.slug}`}
        >
          {fields.map((field, index) => (
            <div key={field.id} id={field.id}>
              <div className="ft-widget-wrapper size-full rounded-page-widget text-neutral-900">
                <div className="bg-page-widget-background shadow-page-widget border-page-widget size-full rounded-page-widget text-page-body-text backdrop-blur-page-widget">
                  <div className="size-full relative p-block">
                    {[
                      'FORM_FIELD_TYPE_HEADING',
                      FormFieldType.HEADING,
                      'HEADING',
                    ].includes(field.content?.kind as any) ? (
                      <BlockHeading
                        field={field}
                        key={index}
                        className="mb-0!"
                      />
                    ) : (
                      <DynamicInput
                        form={form}
                        field={field}
                        key={index}
                        className="mb-0!"
                        labelClassName="text-page-body-text"
                        instructionClassName="text-page-body-muted-text"
                        inputClassName="bg-page-input-background text-page-input-text rounded-page-widget-block border-page-input-border shadow-page-input placeholder:text-page-input-placeholder!"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div>
            <Button
              type="submit"
              loading={mutationCreate.isPending}
              className="w-full"
              variant="blockPrimary"
              size="block"
              id={`form-${formTemplateData?.formTemplate?.slug}-submit`}
            >
              {t('submit')}
              <ArrowRightIcon width={18} height={18} className="ml-2" />
            </Button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <>
      {header}

      {content}
    </>
  )
}
